import { motionJson, type MotionExport } from './motion'
import { modalityJson } from './modality'

/**
 * Die Übergabe an die Trainingsstrecke — ein Satz Dateien, kein Verfahren.
 *
 * WOZU. Die Stationen 3 bis 5 der Werkzeugkette laufen nicht im Browser und
 * nicht in dieser Anwendung: Isaac Sim, Newton und Isaac Lab sind native
 * Programme auf einer Grafikkarte. Was diese Anwendung dafür tun kann, ist,
 * alles bereitzulegen, was sie brauchen — und zwar erzeugt aus dem aktuellen
 * Modellstand, nicht abgetippt.
 *
 * WAS IM BÜNDEL LIEGT UND WARUM GENAU DAS:
 *
 *   – `gen5.xml`  — das MJCF. Newton liest es unmittelbar; seit Newton 1.0 ist
 *                  das nicht mehr das Eingangsformat eines Nebenwerkzeugs,
 *                  sondern das der Trainingsstrecke selbst.
 *   – `gen5.urdf` — der kleinste gemeinsame Nenner. Solange Isaac Lab
 *                  voreingestellt auf der bisherigen Physik läuft, führt der
 *                  Weg dort weiter über die Roboterbeschreibung. Beide
 *                  Eingänge bleiben deshalb offen.
 *   – `motion.json` — die Referenzbewegung. Das Vorbild, gegen das der Regler
 *                  lernt, in der Achsreihenfolge des MJCF.
 *   – `modality.json` — die Kanalbeschreibung für GR00T. Ohne sie ist Gen5 für
 *                  die Aufgabenebene ein Roboter mit siebenundzwanzig
 *                  unbenannten Zahlen.
 *   – `README.md` — was womit zu tun ist, mit den Befehlen.
 *
 * JEDE DATEI TRÄGT DIE MODELLKENNUNG. Die Zäsur der Werkzeugkette liegt hinter
 * Station 2: ab dort gibt es nur noch erzeugte Kopien. Ohne eine Kennung in
 * jeder von ihnen lässt sich hinterher nicht sagen, welcher Punktsatz in
 * welchem Trainingslauf steckte — und zwei Stände sehen gleich aus.
 *
 * WAS NICHT IM BÜNDEL LIEGT: der Trainingscode. Er steht als Python im
 * Repository unter `handoff/`, wird dort gelesen und geändert und gehört
 * versioniert — nicht in einen Download, den jemand einmal auspackt und dann
 * von Hand pflegt. Genau die zweite Wahrheit, die dieses Projekt überall
 * vermeidet.
 */

export interface BundleFile {
  name: string
  /** MIME-Typ für den Download. */
  type: string
  content: string
}

export interface BundleInput {
  /** Name des Modells — bestimmt die Dateinamen. */
  name: string
  mjcf: string
  urdf: string
  /** Die eingestellte Gangart — sie bestimmt `motion.json` und `modality.json`. */
  motion: MotionExport
  /**
   * Die übrigen Gangarten und Bewegungen, je eine eigene Datei.
   *
   * WARUM ALLE UND NICHT NUR DIE EINGESTELLTE. Ein Regler, der nur das Gehen
   * als Vorbild bekommt, lernt das Gehen — und faellt beim ersten Laufschritt
   * um. Die Trainingsstrecke streut über Gangarten; dafür muss sie sie haben.
   *
   * `modality.json` gibt es trotzdem nur EINMAL: die Kanalbeschreibung hängt
   * an der Maschine und nicht an der Bewegung. Zwei Fassungen davon wären zwei
   * Bauformen.
   */
  more?: readonly MotionExport[]
  /** Was das MJCF nicht tragen kann — kommt in die README. */
  gaps: readonly { node: string; axes: readonly string[]; reason: string }[]
}

/** Baut den Satz Dateien. Rein: gleicher Stand, gleiche Dateien. */
export function handoffBundle(input: BundleInput): BundleFile[] {
  return [
    { name: `${input.name}.xml`, type: 'application/xml', content: input.mjcf },
    { name: `${input.name}.urdf`, type: 'application/xml', content: input.urdf },
    { name: 'motion.json', type: 'application/json', content: motionJson(input.motion) },
    ...(input.more ?? []).map((m) => ({
      name: `motion-${m.gait}.json`,
      type: 'application/json',
      content: motionJson(m),
    })),
    { name: 'modality.json', type: 'application/json', content: modalityJson(input.motion) },
    { name: 'README.md', type: 'text/markdown', content: readme(input) },
  ]
}

/**
 * Die Anleitung — mit Zahlen aus dem Stand, nicht aus dem Gedächtnis.
 *
 * SIE WIRD ERZEUGT UND NICHT GEPFLEGT, aus demselben Grund wie das MJCF: eine
 * Anleitung, die 55 kg und 27 Achsen behauptet, während die Datei daneben
 * andere führt, ist schlimmer als keine.
 */
function readme(input: BundleInput): string {
  const m = input.motion
  const angetrieben = m.joints.filter((j) => !j.passive).length
  const federn = m.joints.filter((j) => j.passive)
  const ohneKurve = m.joints.filter((j) => !j.recorded)

  const lines: string[] = []
  lines.push(`# ${input.name.toUpperCase()} — Übergabe an die Trainingsstrecke`)
  lines.push('')
  lines.push('Erzeugt aus dem NEURA-Performance-Simulator. **Nicht von Hand ändern** —')
  lines.push('die Quelle ist der Punktsatz, nicht diese Dateien. Wer hier etwas ändert,')
  lines.push('hat ab diesem Moment zwei Modelle.')
  lines.push('')
  lines.push('## Was hier liegt')
  lines.push('')
  lines.push('| Datei | Wofür |')
  lines.push('|---|---|')
  lines.push(`| \`${input.name}.xml\` | MJCF. Newton und MuJoCo lesen es unmittelbar. |`)
  lines.push(
    `| \`${input.name}.urdf\` | URDF. Für den Weg über Isaac Sim, solange Isaac Lab voreingestellt auf der bisherigen Physik läuft. |`,
  )
  lines.push(
    `| \`motion.json\` | Referenzbewegung (${m.gait}) — das Vorbild, gegen das belohnt wird. |`,
  )
  for (const w of input.more ?? []) {
    lines.push(
      `| \`motion-${w.gait}.json\` | dieselbe Maschine, Gangart ${w.gait}, ${w.speed.toFixed(2)} m/s. |`,
    )
  }
  lines.push(
    '| `modality.json` | Kanalbeschreibung für GR00T: welcher Abschnitt der Zahlenreihe welche Achse ist. |',
  )
  lines.push('')
  lines.push('## Der Modellstand')
  lines.push('')
  lines.push(`- Kennung **\`${m.modelId}\`** — sie steht in jeder Datei dieses Bündels.`)
  lines.push(
    '  Wer sie im Trainingslauf mitführt, kann hinterher sagen, welcher Punktsatz darin steckte.',
  )
  lines.push(`- Gangart **${m.gait}**, ${m.speed.toFixed(2)} m/s`)
  lines.push(`- Masse **${m.mass.toFixed(2)} kg**`)
  lines.push(`- ${m.joints.length} Achsen, davon ${angetrieben} angetrieben`)
  if (federn.length > 0) {
    lines.push(
      `- ${federn.length} passive Federn (${federn.map((j) => `\`${j.name}\``).join(', ')}) — sie tragen ein Moment und lassen sich **nicht kommandieren**. Ein Regler, der sie in seinen Aktionsraum nimmt, lernt gegen ein Stellglied, das es nicht gibt.`,
    )
  }
  lines.push(
    `- Doppelschritt ${m.cycleTime.toFixed(3)} s auf ${m.samples} Stützstellen (${(m.dt * 1000).toFixed(1)} ms)`,
  )
  lines.push('')
  lines.push('## Zwei Dinge, die falsch zu machen sind')
  lines.push('')
  lines.push(
    '**Winkel stehen im Bogenmass.** In `motion.json` und in `keyframe qpos` des MJCF.',
  )
  lines.push(
    'Im MJCF stehen die Gelenk*grenzen* dagegen in Grad (`compiler angle="degree"`) —',
  )
  lines.push('das gilt ausdrücklich NICHT für den Keyframe. An dieser Stelle ist eine frühere')
  lines.push('Fassung des Erzeugers gescheitert, mit einem Schwerpunkt 17 cm zu hoch.')
  lines.push('')
  lines.push('**Die Achsreihenfolge ist die des MJCF.** `motion.json` führt sie unter')
  lines.push('`joints` mit Namen; wer nach Index zuordnet statt nach Namen, dreht bei der')
  lines.push('ersten stillgelegten Achse die falschen Gelenke — und merkt es erst nach dem')
  lines.push('ersten Training.')
  lines.push('')

  if (ohneKurve.length > 0) {
    lines.push('## Achsen ohne Vorbild')
    lines.push('')
    lines.push(
      `${ohneKurve.length} Achsen führt der Referenzgang nicht; ihre Reihe in \`motion.json\``,
    )
    lines.push('ist null. Das ist eine **Vorgabe und keine Bewegung** — wer sie als Vorbild')
    lines.push('nimmt, trainiert einen Regler darauf, die Achse stillzuhalten. Sie stehen mit')
    lines.push('`"recorded": false` da:')
    lines.push('')
    for (const j of ohneKurve) lines.push(`- \`${j.name}\``)
    lines.push('')
  }

  if (input.gaps.length > 0) {
    lines.push('## Was das Modell nicht trägt')
    lines.push('')
    for (const g of input.gaps) {
      lines.push(`- **${g.node}** (${g.axes.join(', ')}): ${g.reason}`)
    }
    lines.push('')
  }

  lines.push('## Die Befehle')
  lines.push('')
  lines.push('```sh')
  lines.push('# 1. Modell prüfen — bevor irgendetwas trainiert wird')
  lines.push(`python -m mujoco.viewer --mjcf ${input.name}.xml`)
  lines.push('')
  lines.push('# 2. Referenzbewegungen in das Datensatzformat der Station 9')
  lines.push('for f in motion*.json; do')
  lines.push('  python handoff/to_lerobot.py "$f" --out "datasets/hs2-${f%.json}"')
  lines.push('done')
  lines.push('')
  lines.push('# 2b. Kanalbeschreibung dazulegen — GR00T sucht sie im Datensatz')
  lines.push('cp modality.json datasets/hs2-walk/meta/')
  lines.push('')
  lines.push('# 3. Training (Station 5). Newton als Physik, MJCF als Modell.')
  lines.push('python handoff/train.py --model gen5.xml --motion motion.json --envs 4096')
  lines.push('')
  lines.push('# 4. Nachtrainieren der Aufgabenebene (Station 10), Gen5 als neue Bauform')
  lines.push(
    'python scripts/launch_finetune.py --dataset-path datasets/hs2-walk \\\n'
      + '    --embodiment-tag new_embodiment --modality-config-path modality.json',
  )
  lines.push('```')
  lines.push('')
  lines.push('Die Skripte liegen im Repository unter `handoff/` und werden dort gepflegt.')
  lines.push('Sie sind bewusst **nicht** in diesem Bündel: ein Trainingsskript, das jemand')
  lines.push('aus einem Download heraus ändert, ist ab dann von der Versionsgeschichte')
  lines.push('abgeschnitten.')
  lines.push('')
  lines.push('## Herkunft')
  lines.push('')
  lines.push(m.provenance)
  lines.push('')
  return lines.join('\n')
}
